<template>
  <section class="pb-2">
    <b-col class="d-flex justify-content-between px-0 pt-25">
      <b-button
        v-b-modal.industry-modal
        variant="primary"
        :disabled="isLoading"
        size="sm"
      >
        <GjIcon
          class="mr-50"
          name="Plus"
        />
        {{ $tk('ClientPortal.Admin.CreateIndustryItem') }}
      </b-button>
    </b-col>
    <b-card
      class="my-50"
      no-body
    >
      <Table
        :data="industries"
        :columns="industryColumns"
        :is-loading="isLoading"
        type-of-table="industryItems"
        :enable-pagination="false"
      />
      <b-modal
        id="modal-delete-industry"
        :ok-title="$tk('ClientPortal.Form.Actions.Delete')"
        :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
        ok-variant="danger"
        cancel-variant="outline-secondary"
        centered
        size="md"
        :title="$tk('ClientPortal.Admin.DeleteIndustry')"
        no-enforce-focus
        @ok="deleteIndustry"
      >
        <span>{{ $tk('ClientPortal.Admin.AreYouSureYouWantToDeleteIndustry') }}</span>
      </b-modal>
      <!-- Add/Edit industries items -->
      <b-modal
        id="industry-modal"
        :title="industryModel.id > 0 ? $tk('ClientPortal.Admin.UpdateIndustryItem'):$tk('ClientPortal.Admin.CreateIndustryItem')"
        :ok-title="industryModel.id > 0 ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.Form.Actions.Create')"
        :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
        centered
        cancel-variant="outline-secondary"
        no-enforce-focus
        :ok-disabled="saving"
        no-close-on-backdrop
        @show="getIndustryModel"
        @ok="updateIndustry"
      >
        <b-form-group
          class="mb-2"
          :label="$tk('ClientPortal.Admin.IndustryName') + ' *'"
          :invalid-feedback="errorMessages['Name']"
        >
          <b-form-input
            v-model="industryModel.name"
            :placeholder="$tk('ClientPortal.Admin.IndustryNamePlaceholder')"
            :state="errorMessages['Name'] == null ? null : false"
          />
        </b-form-group>
        <b-form-group
          class="mb-0"
          :label="$tk('ClientPortal.Admin.IndustryParentName')"
          :invalid-feedback="errorMessages['ParentName']"
        >
          <v-select
            v-model="industryModel.parentId"
            :clearable="false"
            label="text"
            :options="parentIndustries"
            :placeholder="$tk('ClientPortal.Admin.IndustryParentSelectPlaceholder')"
            :state="errorMessages == null ? null : false"
            :reduce="industry => industry.value"
            @change.stop="selectParent"
          />
        </b-form-group>
      </b-modal>
    </b-card>
  </section>
</template>
<script>
import {
  onMounted,
  ref,
  computed,
} from '@vue/composition-api'
import store from '@/store'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import translation from '@/mixins/translate'
import notification from '@/mixins/toastification'
import Table from '@/components/Table.vue'
import IndustryItemsService from '@/api/admin/industries.service'
import vSelect from 'vue-select'

export default {
  components: {
    GjIcon,
    Table,
    vSelect,
  },
  mixins: [notification],
  setup() {
    const industryColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.CreateIndustryItem.Id'),
        field: 'id',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.CreateIndustryItem.Name'),
        field: 'name',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.CreateIndustryItem.ParentName'),
        field: 'parentName',
      },
    ]

    const isLoading = ref(false)
    const industries = computed(() => store.getters.getAdminIndustries)
    const industryModel = ref({})
    const errorMessages = ref({})
    const saving = ref(false)
    const parentIndustries = ref([])

    const fetchParentIndustries = async () => {
      const response = await IndustryItemsService.getParentIndustries()
      parentIndustries.value = response.map(industry => ({
        value: industry.id,
        text: industry.name || 'NoName',
      })).filter(industry => industry.text)
    }

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getAdminIndustries')
      industries.value.searchTerm = ''
      isLoading.value = false
      await fetchParentIndustries()
    })

    const deleteIndustry = async () => {
      isLoading.value = true
      const industry = store.getters.getIndustryModel

      try {
        const response = await IndustryItemsService.deleteIndustry(industry.id)
        if (response.saved) {
          notification.methods.success(response.message)
          await fetchParentIndustries()
        } else {
          notification.methods.error(response.message)
          errorMessages.value = response.message
        }
      } catch (error) {
        notification.methods.error(error.message)
      } finally {
        store.commit('RESET_INDUSTRY_MODEL')
        await store.dispatch('getAdminIndustries')
        isLoading.value = false
        saving.value = false
      }
    }

    const getIndustryModel = () => {
      if (!store.getters.getIndustryModel.id) {
        store.commit('RESET_INDUSTRY_MODEL')
      }
      industryModel.value = store.getters.getIndustryModel
      errorMessages.value = {}
      store.commit('SET_INDUSTRY_MODEL', {})
    }

    const onSearchChange = async value => {
      industries.value.searchTerm = value
    }

    const updateIndustry = async bvModalEvt => {
      bvModalEvt.preventDefault()
      saving.value = true
      try {
        const isEditing = industryModel.value.id === 0
        const response = isEditing ? await IndustryItemsService.updateIndustry(industryModel.value.id, industryModel.value) : await IndustryItemsService.createIndustry(industryModel.value)
        if (response.saved) {
          bvModalEvt.vueTarget.hide('industry-modal')
          if (!isEditing) {
            industryModel.value = {}
          }
          await fetchParentIndustries()
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
          errorMessages.value = response.errors
        }
      } catch (error) {
        notification.methods.error(error.message)
      }
      await store.dispatch('getAdminIndustries')
      saving.value = false
    }

    return {
      isLoading,
      industries,
      industryColumns,
      getIndustryModel,
      industryModel,
      onSearchChange,
      updateIndustry,
      saving,
      errorMessages,
      deleteIndustry,
      parentIndustries,
    }
  },
}
</script>
